import React, { useEffect, useRef, type FC } from "react";

import { useIntl } from "react-intl";
import { useRecoilCallback, useRecoilValue } from "recoil";

import {
  BADGE_KIND_SECONDARY,
  BADGE_TYPE_DEFAULT,
  BADGE_TYPE_SUCCESS,
  Badge,
  DataListHeader,
  DataListRow,
  EmptyState,
  Flex,
  GRID_VARIANT_SMALL,
  Grid,
  GridCell,
  MESSAGE_BOX_VARIANT_DANGER,
  MessageBox,
  PaginationBar,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  TEXT_VARIANT_LIGHT,
  Text,
} from "mailup-web-components";

import {
  fetchTrackingChangeLogsCallback,
  initTrackingChangeLogsCallback,
  resetTrackingChangeLogsCallback,
} from "Pages/RecipientsList/recoil/callbacks/recipient";
import { recipientTrackingChangeLogsState } from "Pages/RecipientsList/recoil/state/recipient";

interface TabTrackingProps {
  idRecipient: number;
}

const COLS = 12;
const SPAN_STATUS = 4;
const SPAN_DATE = 4;
const SPAN_ORIGIN = 4;

const TabTracking: FC<TabTrackingProps> = ({ idRecipient }) => {
  const intl = useIntl();

  const {
    loading,
    status,
    items,
    totalItems,
    pageNumber,
    pageSize,
    orderDescending,
  } = useRecoilValue(recipientTrackingChangeLogsState);

  const initTrackingChangeLogs = useRecoilCallback(
    initTrackingChangeLogsCallback,
    [],
  );
  const fetchTrackingChangeLogs = useRecoilCallback(
    fetchTrackingChangeLogsCallback,
    [],
  );
  const resetTrackingChangeLogs = useRecoilCallback(
    resetTrackingChangeLogsCallback,
    [],
  );

  // Use refs to hold the latest callback without destabilising useEffect deps.
  // useRecoilCallback does not guarantee a stable reference across renders in
  // this version of Recoil, so including the callbacks in the deps array causes
  // an infinite update loop.
  const initRef = useRef(initTrackingChangeLogs);
  initRef.current = initTrackingChangeLogs;
  const resetRef = useRef(resetTrackingChangeLogs);
  resetRef.current = resetTrackingChangeLogs;

  useEffect(() => {
    void initRef.current(idRecipient);
    return () => {
      void resetRef.current();
    };
  }, [idRecipient]);

  const handleChangePage = (page: number): void => {
    void fetchTrackingChangeLogs(idRecipient, page);
  };

  const handleChangePageSize = (size: number): void => {
    void fetchTrackingChangeLogs(idRecipient, 1, size);
  };

  const handleSort = (): void => {
    void fetchTrackingChangeLogs(idRecipient, 1, undefined, !orderDescending);
  };

  const heads = [
    {
      label: intl.formatMessage({
        id: "Load.RecipientsList.TrackingChangeLogStatus",
      }),
      span: SPAN_STATUS,
    },
    {
      label: intl.formatMessage({
        id: "Load.RecipientsList.TrackingChangeLogDate",
      }),
      span: SPAN_DATE,
      sortable: true,
      field: "InsertDate",
    },
    {
      label: intl.formatMessage({
        id: "Load.RecipientsList.TrackingChangeLogOrigin",
      }),
      span: SPAN_ORIGIN,
    },
  ];

  if (status === "error") {
    return (
      <MessageBox variant={MESSAGE_BOX_VARIANT_DANGER}>
        {intl.formatMessage({
          id: "Load.RecipientsList.TrackingChangeLogErrorGeneric",
        })}
      </MessageBox>
    );
  }

  if (status === "ready" && totalItems === 0) {
    return (
      <EmptyState
        description={intl.formatMessage({
          id: "Load.RecipientsList.TrackingChangeLogEmpty",
        })}
      />
    );
  }

  const skeletonCount = items.length > 0 ? items.length : pageSize;

  return (
    <Flex flexDirection="column" gap="0" margin="24px 0 0 0">
      <DataListHeader
        heads={heads}
        cols={COLS}
        gridVariant={GRID_VARIANT_SMALL}
        orderByStatus="InsertDate"
        orderDescendingStatus={orderDescending}
        handleChangeColumnsOrderCallback={handleSort}
      />
      <section>
        {loading &&
          Array.from({ length: skeletonCount }).map((_, idx) => (
            <DataListRow key={`tracking-skeleton-${idx}`}>
              <Grid cols={COLS} variant={GRID_VARIANT_SMALL}>
                <GridCell span={SPAN_STATUS}>
                  <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                </GridCell>
                <GridCell span={SPAN_DATE}>
                  <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                </GridCell>
                <GridCell span={SPAN_ORIGIN}>
                  <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                </GridCell>
              </Grid>
            </DataListRow>
          ))}

        {!loading &&
          items.map((item) => (
            <DataListRow key={item.IdLog}>
              <Grid cols={COLS} variant={GRID_VARIANT_SMALL}>
                <GridCell span={SPAN_STATUS}>
                  <Badge
                    kind="outline"
                    type={
                      item.OpenTrackingEnabled
                        ? BADGE_TYPE_SUCCESS
                        : BADGE_TYPE_DEFAULT
                    }
                    text={intl.formatMessage({
                      id: item.OpenTrackingEnabled
                        ? "Load.MailupCommon.Active"
                        : "Load.RecipientsList.NotTrack",
                    })}
                  />
                </GridCell>
                <GridCell span={SPAN_DATE}>
                  <Text variant={TEXT_VARIANT_LIGHT}>{item.InsertDate}</Text>
                </GridCell>
                <GridCell span={SPAN_ORIGIN}>
                  <Text
                    variant={TEXT_VARIANT_LIGHT}
                    style={{ textTransform: "capitalize" }}
                  >
                    {intl.formatMessage({
                      id:
                        item.IdNLFrom === 0
                          ? "Load.RecipientsList.FromPlatform"
                          : "Load.RecipientsList.FromUsers",
                    })}
                  </Text>
                </GridCell>
              </Grid>
            </DataListRow>
          ))}
      </section>

      {!loading && totalItems > 0 && (
        <PaginationBar
          margin="24px 0 0 0"
          minPageSize={5}
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalItems={totalItems}
          handleChangePage={handleChangePage}
          handleChangePageSize={handleChangePageSize}
          strings={{
            of: intl.formatMessage({ id: "Load.Pager.Of" }),
            label: intl.formatMessage({ id: "Load.Pager.Items" }),
          }}
          formatValues={intl.formatNumber}
        />
      )}
    </Flex>
  );
};

export default TabTracking;
